"use client";

/**
 * OrderEntryWidget — FinTech UX Order Entry Panel (§7.1).
 * Zones: market strip · parameters · risk · actions.
 * Progressive advanced disclosure. Friction for market / high notional.
 * Never marks filled; never costumes buying power zeros as live.
 */
import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { DownOutlined, UpOutlined } from "@ant-design/icons";
import { formatCurrency } from "@/shared/utils/formatters";
import { WidgetChrome } from "./WidgetChrome";
import styles from "./OrderEntryWidget.module.css";

export type OrderSide = "buy" | "sell";
export type OrderType = "market" | "limit" | "stop" | "stop_limit";
export type TimeInForce = "GTC" | "IOC" | "FOK" | "DAY";

export interface OrderEntryPayload {
  symbol: string;
  side: OrderSide;
  orderType: OrderType;
  quantity: number;
  price?: number;
  stopPrice?: number;
  timeInForce: TimeInForce;
  reduceOnly?: boolean;
  postOnly?: boolean;
}

export interface OrderEntryWidgetProps {
  /** Prefill symbol (deep-link) */
  defaultSymbol?: string;
  /** Prefill side e.g. intent=close → sell */
  defaultSide?: OrderSide;
  /** When true, enable reduce-only (close intent) */
  defaultReduceOnly?: boolean;
  /** Last / mark price — null = unknown, never invent */
  marketPrice?: number | null;
  bid?: number | null;
  ask?: number | null;
  /** Available buying power — null = not loaded */
  buyingPower?: number | null;
  currentPosition?: {
    quantity: number;
    averagePrice?: number;
    unrealizedPnl?: number;
  } | null;
  /** Notional above which confirm is required (default 25_000) */
  highNotionalThreshold?: number;
  /** Require confirm for market orders (default true) */
  confirmMarketOrders?: boolean;
  /** Ticket tradable (connected + entitled) */
  tradable?: boolean;
  disabledReason?: string;
  loading?: boolean;
  onSubmit: (order: OrderEntryPayload) => Promise<void>;
  onSymbolChange?: (symbol: string) => void;
  className?: string;
  /** Estimated fee rate (default 0.1%) */
  feeRate?: number;
}

const ADV_KEY = "mechagora.order-entry.advanced";
const QUICK = [25, 50, 75, 100] as const;

function parseNum(v: string): number | null {
  if (v.trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function OrderEntryWidget({
  defaultSymbol = "",
  defaultSide = "buy",
  defaultReduceOnly = false,
  marketPrice = null,
  bid = null,
  ask = null,
  buyingPower = null,
  currentPosition = null,
  highNotionalThreshold = 25_000,
  confirmMarketOrders = true,
  tradable = true,
  disabledReason,
  loading = false,
  onSubmit,
  onSymbolChange,
  className = "",
  feeRate = 0.001,
}: OrderEntryWidgetProps) {
  const baseId = useId();
  const [symbol, setSymbol] = useState(defaultSymbol);
  const [side, setSide] = useState<OrderSide>(defaultSide);
  const [orderType, setOrderType] = useState<OrderType>("limit");
  const [qtyStr, setQtyStr] = useState("");
  const [priceStr, setPriceStr] = useState("");
  const [stopStr, setStopStr] = useState("");
  const [tif, setTif] = useState<TimeInForce>("GTC");
  const [reduceOnly, setReduceOnly] = useState(defaultReduceOnly);
  const [postOnly, setPostOnly] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(ADV_KEY);
      if (v === "1") setAdvanced(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    setSymbol(defaultSymbol);
  }, [defaultSymbol]);

  useEffect(() => {
    setSide(defaultSide);
  }, [defaultSide]);

  useEffect(() => {
    setReduceOnly(defaultReduceOnly);
  }, [defaultReduceOnly]);

  const toggleAdvanced = () => {
    setAdvanced((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(ADV_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const quantity = parseNum(qtyStr);
  const price = parseNum(priceStr);
  const stopPrice = parseNum(stopStr);

  const effectivePrice = useMemo(() => {
    if (orderType === "market") {
      if (side === "buy" && ask != null) return ask;
      if (side === "sell" && bid != null) return bid;
      return marketPrice;
    }
    if (orderType === "limit" || orderType === "stop_limit") {
      return price ?? marketPrice;
    }
    return marketPrice;
  }, [orderType, side, ask, bid, marketPrice, price]);

  const preview = useMemo(() => {
    if (quantity == null || quantity <= 0 || effectivePrice == null || effectivePrice <= 0) {
      return null;
    }
    const notional = quantity * effectivePrice;
    const fee = notional * feeRate;
    const total = side === "buy" ? notional + fee : notional - fee;
    const remaining =
      buyingPower != null && side === "buy" ? buyingPower - total : null;
    return { notional, fee, total, remaining, orderPrice: effectivePrice };
  }, [quantity, effectivePrice, feeRate, side, buyingPower]);

  const exceedsBalance =
    side === "buy" &&
    buyingPower != null &&
    preview != null &&
    preview.remaining != null &&
    preview.remaining < 0;

  const exceedsPosition =
    side === "sell" &&
    currentPosition != null &&
    quantity != null &&
    quantity > currentPosition.quantity;

  const slippageWarn =
    orderType === "market" &&
    bid != null &&
    ask != null &&
    marketPrice != null &&
    marketPrice > 0 &&
    (ask - bid) / marketPrice > 0.002;

  const highNotional =
    preview != null && preview.notional >= highNotionalThreshold;

  const needsConfirm =
    (confirmMarketOrders && orderType === "market") || highNotional;

  const blockReason = useMemo(() => {
    if (!tradable) {
      return disabledReason || "Ticket offline — trading unavailable.";
    }
    if (!symbol.trim()) return "Enter a symbol.";
    if (quantity == null || quantity <= 0) return "Enter a valid quantity.";
    if (
      (orderType === "limit" || orderType === "stop_limit") &&
      (price == null || price <= 0)
    ) {
      return "Enter a valid limit price.";
    }
    if (
      (orderType === "stop" || orderType === "stop_limit") &&
      (stopPrice == null || stopPrice <= 0)
    ) {
      return "Enter a valid stop price.";
    }
    if (exceedsBalance) {
      return `Size exceeds available ${formatCurrency(buyingPower ?? 0)}.`;
    }
    if (exceedsPosition) {
      return `Quantity exceeds position ${currentPosition?.quantity ?? 0}.`;
    }
    return null;
  }, [
    tradable,
    disabledReason,
    symbol,
    quantity,
    orderType,
    price,
    stopPrice,
    exceedsBalance,
    exceedsPosition,
    buyingPower,
    currentPosition,
  ]);

  const canSubmit =
    !blockReason && !submitting && !loading && !confirmOpen;

  const applyQuick = useCallback(
    (pct: number) => {
      if (side === "buy") {
        if (buyingPower == null || effectivePrice == null || effectivePrice <= 0) {
          return;
        }
        const funds = buyingPower * (pct / 100);
        const q = Math.floor((funds * 0.999) / effectivePrice * 10000) / 10000;
        setQtyStr(String(Math.max(0, q)));
      } else if (currentPosition) {
        const q =
          Math.floor(currentPosition.quantity * (pct / 100) * 10000) / 10000;
        setQtyStr(String(Math.max(0, q)));
      }
    },
    [side, buyingPower, effectivePrice, currentPosition]
  );

  const buildPayload = useCallback((): OrderEntryPayload | null => {
    if (blockReason || quantity == null) return null;
    const payload: OrderEntryPayload = {
      symbol: symbol.trim().toUpperCase(),
      side,
      orderType,
      quantity,
      timeInForce: tif,
      reduceOnly: advanced ? reduceOnly : defaultReduceOnly || undefined,
      postOnly: advanced ? postOnly : undefined,
    };
    if (orderType === "limit" || orderType === "stop_limit") {
      if (price == null) return null;
      payload.price = price;
    }
    if (orderType === "stop" || orderType === "stop_limit") {
      if (stopPrice == null) return null;
      payload.stopPrice = stopPrice;
    }
    return payload;
  }, [
    blockReason,
    quantity,
    symbol,
    side,
    orderType,
    tif,
    advanced,
    reduceOnly,
    postOnly,
    defaultReduceOnly,
    price,
    stopPrice,
  ]);

  const fireSubmit = useCallback(async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});
    try {
      await onSubmit(payload);
      setQtyStr("");
      setPriceStr("");
      setStopStr("");
      setConfirmOpen(false);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Order rejected. Try again.";
      setSubmitError(msg);
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  }, [buildPayload, onSubmit]);

  const handlePrimary = useCallback(() => {
    // local validation feedback
    const next: Record<string, string> = {};
    if (!symbol.trim()) next.symbol = "Enter a symbol";
    if (quantity == null || quantity <= 0) next.quantity = "Enter quantity";
    if (
      (orderType === "limit" || orderType === "stop_limit") &&
      (price == null || price <= 0)
    ) {
      next.price = "Enter limit price";
    }
    if (
      (orderType === "stop" || orderType === "stop_limit") &&
      (stopPrice == null || stopPrice <= 0)
    ) {
      next.stopPrice = "Enter stop price";
    }
    if (exceedsBalance) {
      next.quantity = `Exceeds available ${formatCurrency(buyingPower ?? 0)}`;
    }
    if (exceedsPosition) {
      next.quantity = `Exceeds position ${currentPosition?.quantity ?? 0}`;
    }
    setFieldErrors(next);
    if (Object.keys(next).length > 0 || blockReason) return;

    if (needsConfirm) {
      setConfirmOpen(true);
      return;
    }
    void fireSubmit();
  }, [
    symbol,
    quantity,
    orderType,
    price,
    stopPrice,
    exceedsBalance,
    exceedsPosition,
    buyingPower,
    currentPosition,
    blockReason,
    needsConfirm,
    fireSubmit,
  ]);

  const submitLabel = useMemo(() => {
    const sym = symbol.trim().toUpperCase() || "—";
    if (submitting || loading) return "Submitting…";
    return side === "buy" ? `Buy ${sym}` : `Sell ${sym}`;
  }, [symbol, side, submitting, loading]);

  const markDisplay =
    marketPrice != null && Number.isFinite(marketPrice)
      ? formatCurrency(marketPrice)
      : "—";

  return (
    <WidgetChrome
      label="Order entry"
      meta="Ticket"
      aria-label="Order entry panel"
      className={`${styles.ticket} ${className}`.trim()}
      emphasis="primary"
    >
      {!tradable ? (
        <div className={styles.offlineBanner} role="status">
          {disabledReason || "Trading offline or not entitled. Ticket locked."}
        </div>
      ) : null}

      {/* Zone: Market */}
      <div className={styles.zone}>
        <span className={styles.zoneLabel}>Market</span>
        <div className={styles.marketStrip}>
          <div className={`${styles.field} ${styles.symbolField}`}>
            <label className={styles.label} htmlFor={`${baseId}-symbol`}>
              Symbol
            </label>
            <input
              id={`${baseId}-symbol`}
              className={`${styles.input} ${
                fieldErrors.symbol ? styles.inputError : ""
              }`}
              value={symbol}
              autoComplete="off"
              spellCheck={false}
              disabled={!tradable || submitting}
              onChange={(e) => {
                const v = e.target.value.toUpperCase();
                setSymbol(v);
                onSymbolChange?.(v);
              }}
              aria-invalid={Boolean(fieldErrors.symbol)}
              aria-describedby={
                fieldErrors.symbol ? `${baseId}-symbol-err` : undefined
              }
            />
            {fieldErrors.symbol ? (
              <span id={`${baseId}-symbol-err`} className={styles.errorText}>
                {fieldErrors.symbol}
              </span>
            ) : null}
          </div>

          <div className={styles.sideToggle} role="group" aria-label="Order side">
            <button
              type="button"
              className={`${styles.sideBtn} ${styles.sideBuy} ${
                side === "buy" ? styles.sideBuyActive : ""
              }`}
              aria-pressed={side === "buy"}
              disabled={!tradable || submitting}
              onClick={() => setSide("buy")}
            >
              Buy
            </button>
            <button
              type="button"
              className={`${styles.sideBtn} ${styles.sideSell} ${
                side === "sell" ? styles.sideSellActive : ""
              }`}
              aria-pressed={side === "sell"}
              disabled={!tradable || submitting}
              onClick={() => setSide("sell")}
            >
              Sell
            </button>
          </div>

          <div>
            <span className={styles.markMuted}>Last</span>
            <span className={styles.markReadout}>{markDisplay}</span>
          </div>
        </div>
      </div>

      {/* Zone: Parameters */}
      <div className={styles.zone}>
        <span className={styles.zoneLabel}>Parameters</span>
        <div className={styles.fieldGrid}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${baseId}-type`}>
              Order type
            </label>
            <select
              id={`${baseId}-type`}
              className={styles.select}
              value={orderType}
              disabled={!tradable || submitting}
              onChange={(e) => setOrderType(e.target.value as OrderType)}
            >
              <option value="limit">Limit</option>
              <option value="market">Market</option>
              <option value="stop">Stop</option>
              <option value="stop_limit">Stop limit</option>
            </select>
            <span className={styles.helper}>
              {orderType === "market"
                ? "Fills at market — slippage possible"
                : orderType === "limit"
                  ? "Only at your price or better"
                  : "Triggered when stop is reached"}
            </span>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor={`${baseId}-qty`}>
              Quantity
            </label>
            <input
              id={`${baseId}-qty`}
              className={`${styles.input} ${styles.inputRight} ${
                fieldErrors.quantity ? styles.inputError : ""
              }`}
              inputMode="decimal"
              value={qtyStr}
              disabled={!tradable || submitting}
              onChange={(e) => setQtyStr(e.target.value)}
              placeholder="0.0000"
              aria-invalid={Boolean(fieldErrors.quantity)}
              aria-describedby={
                fieldErrors.quantity ? `${baseId}-qty-err` : undefined
              }
            />
            {fieldErrors.quantity ? (
              <span id={`${baseId}-qty-err`} className={styles.errorText}>
                {fieldErrors.quantity}
              </span>
            ) : side === "sell" && currentPosition ? (
              <span className={styles.helper}>
                Available: {currentPosition.quantity}
              </span>
            ) : buyingPower != null ? (
              <span className={styles.helper}>
                Buying power: {formatCurrency(buyingPower)}
              </span>
            ) : (
              <span className={styles.helper}>Buying power pending</span>
            )}
          </div>

          {(orderType === "limit" || orderType === "stop_limit") && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`${baseId}-price`}>
                Limit price
              </label>
              <input
                id={`${baseId}-price`}
                className={`${styles.input} ${styles.inputRight} ${
                  fieldErrors.price ? styles.inputError : ""
                }`}
                inputMode="decimal"
                value={priceStr}
                disabled={!tradable || submitting}
                onChange={(e) => setPriceStr(e.target.value)}
                placeholder="0.00"
              />
              {fieldErrors.price ? (
                <span className={styles.errorText}>{fieldErrors.price}</span>
              ) : null}
            </div>
          )}

          {(orderType === "stop" || orderType === "stop_limit") && (
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`${baseId}-stop`}>
                Stop price
              </label>
              <input
                id={`${baseId}-stop`}
                className={`${styles.input} ${styles.inputRight} ${
                  fieldErrors.stopPrice ? styles.inputError : ""
                }`}
                inputMode="decimal"
                value={stopStr}
                disabled={!tradable || submitting}
                onChange={(e) => setStopStr(e.target.value)}
                placeholder="0.00"
              />
              {fieldErrors.stopPrice ? (
                <span className={styles.errorText}>{fieldErrors.stopPrice}</span>
              ) : null}
            </div>
          )}

          <div className={`${styles.field} ${styles.fieldFull}`}>
            <span className={styles.label}>Quick size</span>
            <div className={styles.quickRow} role="group" aria-label="Quick size">
              {QUICK.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={styles.quickBtn}
                  disabled={
                    !tradable ||
                    submitting ||
                    (side === "buy"
                      ? buyingPower == null
                      : !currentPosition)
                  }
                  onClick={() => applyQuick(p)}
                >
                  {p}%
                </button>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          className={styles.advancedToggle}
          onClick={toggleAdvanced}
          aria-expanded={advanced}
        >
          {advanced ? <UpOutlined aria-hidden /> : <DownOutlined aria-hidden />}
          {advanced ? "Hide advanced" : "Advanced"}
        </button>

        {advanced ? (
          <div className={styles.advancedPanel}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={`${baseId}-tif`}>
                Time in force
              </label>
              <select
                id={`${baseId}-tif`}
                className={styles.select}
                value={tif}
                disabled={!tradable || submitting}
                onChange={(e) => setTif(e.target.value as TimeInForce)}
              >
                <option value="GTC">GTC</option>
                <option value="DAY">DAY</option>
                <option value="IOC">IOC</option>
                <option value="FOK">FOK</option>
              </select>
            </div>
            <div className={styles.switchRow}>
              <span className={styles.label}>Reduce only</span>
              <input
                type="checkbox"
                checked={reduceOnly}
                disabled={!tradable || submitting}
                onChange={(e) => setReduceOnly(e.target.checked)}
                aria-label="Reduce only"
              />
            </div>
            <div className={styles.switchRow}>
              <span className={styles.label}>Post only</span>
              <input
                type="checkbox"
                checked={postOnly}
                disabled={!tradable || submitting}
                onChange={(e) => setPostOnly(e.target.checked)}
                aria-label="Post only"
              />
            </div>
          </div>
        ) : null}
      </div>

      {/* Zone: Risk — always visible */}
      <div className={styles.zone} aria-live="polite">
        <span className={styles.zoneLabel}>Risk summary</span>
        <div className={styles.riskGrid}>
          <div className={styles.riskRow}>
            <span className={styles.riskKey}>Est. price</span>
            <span className={styles.riskVal}>
              {preview ? formatCurrency(preview.orderPrice) : "—"}
            </span>
          </div>
          <div className={styles.riskRow}>
            <span className={styles.riskKey}>Notional</span>
            <span
              className={`${styles.riskVal} ${
                highNotional ? styles.riskWarn : ""
              }`}
            >
              {preview ? formatCurrency(preview.notional) : "—"}
            </span>
          </div>
          <div className={styles.riskRow}>
            <span className={styles.riskKey}>Est. fees</span>
            <span className={styles.riskVal}>
              {preview ? formatCurrency(preview.fee) : "—"}
            </span>
          </div>
          <div className={styles.riskRow}>
            <span className={styles.riskKey}>
              {side === "buy" ? "Est. cost" : "Est. proceeds"}
            </span>
            <span className={styles.riskVal}>
              {preview ? formatCurrency(preview.total) : "—"}
            </span>
          </div>
          <div className={styles.riskRow}>
            <span className={styles.riskKey}>Buying power left</span>
            <span
              className={`${styles.riskVal} ${
                exceedsBalance ? styles.riskBad : ""
              }`}
            >
              {side === "buy" && preview?.remaining != null
                ? formatCurrency(preview.remaining)
                : side === "buy" && buyingPower == null
                  ? "—"
                  : "n/a"}
            </span>
          </div>
          <div className={styles.riskRow}>
            <span className={styles.riskKey}>Margin / liq</span>
            <span className={styles.riskVal}>n/a</span>
          </div>
        </div>
        {slippageWarn ? (
          <span className={styles.warnText}>
            Wide spread — consider a limit order.
          </span>
        ) : null}
        {exceedsBalance ? (
          <span className={styles.errorText} role="alert">
            Size exceeds available {formatCurrency(buyingPower ?? 0)}.
          </span>
        ) : null}
      </div>

      {/* Zone: Actions */}
      <div className={styles.zone}>
        <span className={styles.zoneLabel}>Actions</span>
        <div className={styles.actions}>
          {confirmOpen && preview ? (
            <div className={styles.confirmBox} role="alertdialog" aria-label="Confirm order">
              <strong>
                Confirm {side.toUpperCase()} {symbol.trim().toUpperCase()}
              </strong>
              <div className={styles.riskGrid}>
                <div className={styles.riskRow}>
                  <span className={styles.riskKey}>Type</span>
                  <span className={styles.riskVal}>{orderType}</span>
                </div>
                <div className={styles.riskRow}>
                  <span className={styles.riskKey}>Qty</span>
                  <span className={styles.riskVal}>{quantity}</span>
                </div>
                <div className={styles.riskRow}>
                  <span className={styles.riskKey}>Est. cost</span>
                  <span className={styles.riskVal}>
                    {formatCurrency(preview.total)}
                  </span>
                </div>
                <div className={styles.riskRow}>
                  <span className={styles.riskKey}>Fees</span>
                  <span className={styles.riskVal}>
                    {formatCurrency(preview.fee)}
                  </span>
                </div>
              </div>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.confirmCancel}
                  onClick={() => setConfirmOpen(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.confirmOk}
                  onClick={() => void fireSubmit()}
                  disabled={submitting}
                >
                  {submitting ? "Submitting…" : "Confirm order"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className={`${styles.submitBtn} ${
                side === "buy" ? styles.submitBuy : styles.submitSell
              }`}
              disabled={!canSubmit}
              onClick={handlePrimary}
              aria-busy={submitting || loading}
            >
              {submitLabel}
            </button>
          )}
          {submitError ? (
            <span className={styles.blockReason} role="alert">
              {submitError}
            </span>
          ) : blockReason && tradable ? (
            <span className={styles.blockReason}>{blockReason}</span>
          ) : null}
        </div>
      </div>
    </WidgetChrome>
  );
}
